import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { tableStyles } from '../ui';
import {
  getDirectSoldAdsStatus,
  getDirectSoldAdsMonths,
  getDirectSoldAdsRollup,
  getDirectSoldAdsChannelMonth,
  startDirectSoldAdsBackfill,
  startDirectSoldAdsEnrichment,
  startYoutubeAnalyticsBackfill,
  refreshDirectSoldAdsChannelNames,
  DsAdMonthlyRollup,
  DsAdRecord,
  DsAdFormat,
  DsAdFormatMix,
  DsAdStatus,
} from '../../services/adminApi';

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

const FORMAT_LABEL: Record<DsAdFormat, string> = {
  'skippable-video': 'Skippable',
  'non-skippable-15s': ':15',
  'non-skippable-30s': ':30',
  'bumper-6s': 'Bumper',
  'other': 'Other',
};
const FORMAT_ORDER: DsAdFormat[] = [
  'skippable-video',
  'non-skippable-15s',
  'non-skippable-30s',
  'bumper-6s',
  'other',
];

const fmtInt = (n: number): string => (n || 0).toLocaleString('en-US');
const fmtUsd = (n?: number): string =>
  n == null || isNaN(n) ? '-' : `$${Math.round(n).toLocaleString('en-US')}`;
const fmtImpr = (n: number): string => {
  if (!n) return '0';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(Math.round(n));
};
const ecpm = (rev: number, impr: number): string =>
  impr > 0 ? `$${((rev / impr) * 1000).toFixed(2)}` : '-';
const monthLabel = (m: string): string => {
  const [y, mo] = m.split('-');
  const names = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${names[parseInt(mo, 10)] || mo} ${y}`;
};

const formatMixSummary = (mix?: DsAdFormatMix): string => {
  if (!mix) return '';
  return FORMAT_ORDER.filter((f) => (mix[f] || 0) > 0)
    .map((f) => `${FORMAT_LABEL[f]} ${mix[f]}`)
    .join(' · ');
};

type View = 'channels' | 'advertisers' | 'campaigns';

// Advertiser aggregate within a single channel (computed client-side from records).
interface AdvertiserInChannel {
  advertiser: string;
  advertiserId: string;
  records: DsAdRecord[];
  impressions: number;
  revenueUsd: number;
}

const DirectSoldAdsTab: React.FC = () => {
  const [status, setStatus] = useState<DsAdStatus | null>(null);
  const [months, setMonths] = useState<string[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [rollup, setRollup] = useState<DsAdMonthlyRollup | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [view, setView] = useState<View>('channels');
  const [search, setSearch] = useState('');

  // Drill-down state
  const [expandedChannelId, setExpandedChannelId] = useState<string | null>(null);
  const [expandedAdvKey, setExpandedAdvKey] = useState<string | null>(null);
  const [channelRecords, setChannelRecords] = useState<Record<string, DsAdRecord[]>>({});
  const [channelLoading, setChannelLoading] = useState<string | null>(null);

  // Job feedback
  const [jobMsg, setJobMsg] = useState<string | null>(null);

  const loadStatus = useCallback(async () => {
    try {
      setStatus(await getDirectSoldAdsStatus());
    } catch {
      /* non-fatal */
    }
  }, []);

  const loadRollup = useCallback(async (month: string) => {
    if (!month) return;
    setLoading(true);
    setError(null);
    setExpandedChannelId(null);
    setExpandedAdvKey(null);
    setChannelRecords({});
    try {
      const res = await getDirectSoldAdsRollup(month);
      setRollup(res.rollup);
      if (!res.rollup) setError(`No ad data stored for ${monthLabel(month)} yet. Run a backfill to populate it.`);
    } catch (e: any) {
      setError(e?.message || 'Failed to load ad data');
      setRollup(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      await loadStatus();
      try {
        const { months: ms } = await getDirectSoldAdsMonths();
        setMonths(ms);
        if (ms.length) {
          setSelectedMonth(ms[0]);
          await loadRollup(ms[0]);
        }
      } catch (e: any) {
        setError(e?.message || 'Failed to load months');
      }
    })();
  }, [loadStatus, loadRollup]);

  // Poll status while a job is in flight; reload when it finishes.
  const inFlight = !!(status?.backfillInFlight || status?.enrichmentInFlight || status?.youtubeCaptureInFlight);
  useEffect(() => {
    if (!inFlight) return;
    const t = setInterval(async () => {
      const prev = inFlight;
      const s = await getDirectSoldAdsStatus().catch(() => null);
      if (s) setStatus(s);
      if (prev && s && !s.backfillInFlight && !s.enrichmentInFlight && !s.youtubeCaptureInFlight) {
        setJobMsg('Job finished — refreshing.');
        const { months: ms } = await getDirectSoldAdsMonths().catch(() => ({ months }));
        setMonths(ms);
        await loadRollup(selectedMonth || ms[0] || '');
      }
    }, 5000);
    return () => clearInterval(t);
  }, [inFlight, months, selectedMonth, loadRollup]);

  const onMonthChange = (m: string) => {
    setSelectedMonth(m);
    loadRollup(m);
  };

  // rowKey = the stable per-row identity (channelId, or a name: fallback for unmapped
  // channels). Open-state and the records cache BOTH key off rowKey so that unmapped
  // channels — which all share channelId === '' — don't collapse onto one expand state.
  const toggleChannel = async (rowKey: string, channelId: string) => {
    if (expandedChannelId === rowKey) {
      setExpandedChannelId(null);
      setExpandedAdvKey(null);
      return;
    }
    setExpandedChannelId(rowKey);
    setExpandedAdvKey(null);
    if (!channelRecords[rowKey]) {
      setChannelLoading(rowKey);
      try {
        // Unresolved channels have no channelId; their records aren't queryable by id.
        const res = channelId
          ? await getDirectSoldAdsChannelMonth(selectedMonth, channelId)
          : { records: [] as DsAdRecord[] };
        setChannelRecords((prev) => ({ ...prev, [rowKey]: res.records }));
      } catch {
        setChannelRecords((prev) => ({ ...prev, [rowKey]: [] }));
      } finally {
        setChannelLoading(null);
      }
    }
  };

  const advertisersForChannel = (rowKey: string): AdvertiserInChannel[] => {
    const recs = channelRecords[rowKey] || [];
    const byAdv = new Map<string, AdvertiserInChannel>();
    for (const r of recs) {
      const k = r.advertiserId || `name:${r.advertiser.toLowerCase()}`;
      let a = byAdv.get(k);
      if (!a) {
        a = { advertiser: r.advertiser, advertiserId: r.advertiserId, records: [], impressions: 0, revenueUsd: 0 };
        byAdv.set(k, a);
      }
      a.records.push(r);
      a.impressions += r.impressions;
      a.revenueUsd += r.mediaSalesRevenueUsd;
    }
    return Array.from(byAdv.values()).sort((a, b) => b.impressions - a.impressions);
  };

  const runBackfill = async () => {
    if (!window.confirm('Backfill AD history scrapes every channel’s Monthly Tracker (Google Sheets) to rebuild the ad-record warehouse. It is throttled to respect Sheets quota and runs in the background (several minutes). This does NOT touch YouTube Analytics. Continue?')) return;
    try {
      await startDirectSoldAdsBackfill({});
      setJobMsg('Ad-history backfill started — throttled, runs in the background.');
      await loadStatus();
    } catch (e: any) {
      setJobMsg(e?.message || 'Failed to start ad backfill');
    }
  };

  // Default the YouTube backfill start to ~12 completed months back.
  const defaultYtFrom = (): string => {
    const d = new Date();
    d.setMonth(d.getMonth() - 13);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  };

  const runYoutubeBackfill = async () => {
    const from = window.prompt(
      'Backfill YOUTUBE ANALYTICS (per-channel monthly views / watch-time / subscribers for Channel Intelligence) from which month? Format YYYY-MM. Runs through the latest completed month, in the background. This is separate from the ad-tracker backfill.',
      defaultYtFrom()
    );
    if (from == null) return; // cancelled
    const m = from.trim();
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(m)) {
      setJobMsg('Enter the start month as YYYY-MM (e.g. 2025-06).');
      return;
    }
    try {
      await startYoutubeAnalyticsBackfill({ from: m });
      setJobMsg(`YouTube Analytics backfill started from ${monthLabel(m)} — runs in the background.`);
      await loadStatus();
    } catch (e: any) {
      setJobMsg(e?.message || 'Failed to start YouTube backfill');
    }
  };

  const runEnrichment = async () => {
    try {
      await startDirectSoldAdsEnrichment(selectedMonth ? { month: selectedMonth } : {});
      setJobMsg(`Campaign enrichment started for ${selectedMonth ? monthLabel(selectedMonth) : 'all months'}.`);
      await loadStatus();
    } catch (e: any) {
      setJobMsg(e?.message || 'Failed to start enrichment');
    }
  };

  const runRefreshNames = async () => {
    if (!window.confirm('Re-apply current channel names from the channel mapping to all months? Make sure the Channel Mapping tab already reflects the new names.')) return;
    try {
      const { monthsUpdated, changes } = await refreshDirectSoldAdsChannelNames();
      if (!changes.length) {
        setJobMsg('Channel names already match the mapping — nothing to update.');
      } else {
        const summary = changes.map((c) => `“${c.from}” → “${c.to}”`).join(', ');
        setJobMsg(`Renamed ${changes.length} channel(s) across ${monthsUpdated} month(s): ${summary}`);
      }
      await loadRollup(selectedMonth);
    } catch (e: any) {
      setJobMsg(e?.message || 'Failed to refresh channel names');
    }
  };

  const q = search.trim().toLowerCase();
  const totalChannels = rollup?.totals.channelCount || 0;

  const channels = useMemo(() => {
    const list = rollup?.channels || [];
    return q ? list.filter((c) => c.channelName.toLowerCase().includes(q)) : list;
  }, [rollup, q]);

  const advertisers = useMemo(() => {
    const list = rollup?.advertisers || [];
    return q ? list.filter((a) => a.advertiser.toLowerCase().includes(q)) : list;
  }, [rollup, q]);

  const campaigns = useMemo(() => {
    const list = rollup?.campaigns || [];
    return q
      ? list.filter((c) => (c.campaignName || c.gamOrderId).toLowerCase().includes(q))
      : list;
  }, [rollup, q]);

  // CMS attribution is best-effort and currently always 'unknown' (cross-CMS is
  // deferred). Only surface the CMS column / cross-CMS badge once real data exists,
  // so we don't ship a dead, always-empty feature.
  const hasCmsData = useMemo(
    () => (rollup?.campaigns || []).some((c) => c.cmsList.some((x) => x !== 'unknown')),
    [rollup]
  );

  return (
    <div style={styles.wrap}>
      {/* Controls */}
      <div style={styles.controls}>
        <select
          value={selectedMonth}
          onChange={(e) => onMonthChange(e.target.value)}
          style={styles.select}
          disabled={loading || !months.length}
        >
          {months.length ? (
            months.map((m) => (
              <option key={m} value={m}>{monthLabel(m)}</option>
            ))
          ) : (
            <option value="">No data</option>
          )}
        </select>

        <input
          type="text"
          placeholder="Search channel, advertiser, or campaign…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={styles.searchInput}
        />

        <div style={styles.viewToggle}>
          {(['channels', 'advertisers', 'campaigns'] as View[]).map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              style={{ ...styles.viewBtn, ...(view === v ? styles.viewBtnActive : {}) }}
            >
              {v[0].toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>

        <div style={{ flex: 1 }} />

        <button onClick={() => loadRollup(selectedMonth)} style={styles.btn} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
        {status?.canManage && (
          <>
            <button onClick={runRefreshNames} style={styles.btn} disabled={inFlight} title="Re-apply current channel names from the channel mapping">
              Refresh names
            </button>
            <button onClick={runEnrichment} style={styles.btn} disabled={inFlight} title="Resolve campaigns from GAM">
              Enrich campaigns
            </button>
            <button onClick={runYoutubeBackfill} style={styles.btn} disabled={inFlight} title="Capture per-channel monthly YouTube Analytics (views, watch-time, subscribers) for Channel Intelligence — separate from the ad-tracker scrape">
              {status.youtubeCaptureInFlight ? 'Capturing YouTube…' : 'Backfill YouTube'}
            </button>
            <button onClick={runBackfill} style={styles.btnPrimary} disabled={inFlight} title="Scrape Monthly Tracker sheets to rebuild the Direct Sold ad-record warehouse (throttled)">
              {status.backfillInFlight ? 'Backfilling ads…' : 'Backfill ad history'}
            </button>
          </>
        )}
      </div>

      {jobMsg && <div style={styles.banner}>{jobMsg}</div>}
      {inFlight && <div style={styles.banner}>A background job is running… this view will refresh when it completes.</div>}
      {error && <div style={styles.error}>{error}</div>}

      {/* Stats strip */}
      {rollup && (
        <div style={styles.stats}>
          <Stat label="Advertisers" value={fmtInt(rollup.totals.advertiserCount)} />
          <Stat label="Channels" value={fmtInt(rollup.totals.channelCount)} />
          <Stat label="Campaigns" value={fmtInt(rollup.totals.campaignCount)} hint={rollup.totals.campaignCount ? undefined : 'run enrichment'} />
          <Stat label="Impressions" value={fmtImpr(rollup.totals.impressions)} />
          <Stat label="Media Sales" value={fmtUsd(rollup.totals.revenueUsd)} />
          <Stat label="Format mix" value={formatMixSummary(rollup.formatMix) || '—'} wide />
          {rollup.totals.unresolvedChannels > 0 && (
            <Stat label="Unmapped" value={fmtInt(rollup.totals.unresolvedChannels)} hint="channels w/o id" />
          )}
        </div>
      )}

      {/* Views */}
      {rollup && view === 'channels' && (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.th, width: 30 }} />
                <th style={styles.thLeft}>Channel</th>
                <th style={styles.th}>Advertisers</th>
                <th style={styles.th}>Ads</th>
                <th style={styles.th}>Impressions</th>
                <th style={styles.th}>Media Sales</th>
              </tr>
            </thead>
            <tbody>
              {channels.map((c) => {
                const cid = c.channelId || `name:${c.channelName}`;
                const isOpen = expandedChannelId === cid;
                const advs = isOpen ? advertisersForChannel(cid) : [];
                return (
                  <React.Fragment key={cid}>
                    <tr
                      onClick={() => toggleChannel(cid, c.channelId)}
                      style={{ ...styles.tr, cursor: 'pointer', background: isOpen ? 'var(--bg-surface-3)' : undefined }}
                    >
                      <td style={styles.td}>{isOpen ? '▼' : '▶'}</td>
                      <td style={styles.tdLeft}>
                        {c.channelName}
                        {!c.channelId && <span style={styles.tag}>unmapped</span>}
                      </td>
                      <td style={styles.td}>{fmtInt(c.advertiserCount)}</td>
                      <td style={styles.td}>{fmtInt(c.recordCount)}</td>
                      <td style={styles.td}>{fmtImpr(c.impressions)}</td>
                      <td style={styles.td}>{fmtUsd(c.revenueUsd)}</td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={6} style={styles.expandTd}>
                          {channelLoading === cid ? (
                            <div style={styles.muted}>Loading advertisers…</div>
                          ) : !c.channelId ? (
                            <div style={styles.muted}>
                              This channel isn’t mapped to a YouTube ID, so its line items can’t be listed.
                              Fix the channel mapping (Channel Mapping tab) and re-run the backfill.
                            </div>
                          ) : advs.length === 0 ? (
                            <div style={styles.muted}>No line-item records for this channel/month.</div>
                          ) : (
                            <table style={styles.subTable}>
                              <tbody>
                                {advs.map((a) => {
                                  const ak = a.advertiserId || `name:${a.advertiser.toLowerCase()}`;
                                  const advOpen = expandedAdvKey === ak;
                                  return (
                                    <React.Fragment key={ak}>
                                      <tr
                                        onClick={() => setExpandedAdvKey(advOpen ? null : ak)}
                                        style={{ ...styles.subTr, cursor: 'pointer' }}
                                      >
                                        <td style={{ ...styles.subTd, width: 24 }}>{advOpen ? '▼' : '▶'}</td>
                                        <td style={styles.subTdLeft}><strong>{a.advertiser || '(no advertiser)'}</strong></td>
                                        <td style={styles.subTd}>{a.records.length} line items</td>
                                        <td style={styles.subTd}>{fmtImpr(a.impressions)} impr</td>
                                        <td style={styles.subTd}>{fmtUsd(a.revenueUsd)}</td>
                                      </tr>
                                      {advOpen && a.records.map((r) => (
                                        <tr key={r.id} style={styles.liTr}>
                                          <td style={styles.subTd} />
                                          <td style={styles.subTdLeft} colSpan={1}>
                                            <span style={styles.fmtChip}>{FORMAT_LABEL[r.adFormat]}</span>
                                            {r.creativeBrand && <span style={styles.brand}>{r.creativeBrand}</span>}
                                            <div style={styles.liName}>{r.lineItemName || r.lineItemId}</div>
                                            {r.campaignName && <div style={styles.campaign}>Campaign: {r.campaignName}</div>}
                                          </td>
                                          <td style={styles.subTd}>{fmtInt(r.clicks)} clicks</td>
                                          <td style={styles.subTd}>{fmtImpr(r.impressions)} impr</td>
                                          <td style={styles.subTd}>{ecpm(r.mediaSalesRevenueUsd, r.impressions)} eCPM</td>
                                        </tr>
                                      ))}
                                    </React.Fragment>
                                  );
                                })}
                              </tbody>
                            </table>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {rollup && view === 'advertisers' && (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.thLeft}>Advertiser</th>
                <th style={styles.th}>Channels</th>
                <th style={styles.th}>% of network</th>
                <th style={styles.th}>Ads</th>
                <th style={styles.th}>Impressions</th>
                <th style={styles.th}>Media Sales</th>
                <th style={styles.thLeft}>Format mix</th>
              </tr>
            </thead>
            <tbody>
              {advertisers.map((a) => (
                <tr key={a.advertiserId || a.advertiser} style={styles.tr}>
                  <td style={styles.tdLeft}>{a.advertiser || '(no advertiser)'}</td>
                  <td style={styles.td}>{fmtInt(a.channelCount)}</td>
                  <td style={styles.td}>{totalChannels ? `${Math.round((a.channelCount / totalChannels) * 100)}%` : '-'}</td>
                  <td style={styles.td}>{fmtInt(a.recordCount)}</td>
                  <td style={styles.td}>{fmtImpr(a.impressions)}</td>
                  <td style={styles.td}>{fmtUsd(a.revenueUsd)}</td>
                  <td style={styles.tdLeftSmall}>{formatMixSummary(a.formatMix) || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {rollup && view === 'campaigns' && (
        <div style={styles.tableWrap}>
          {rollup.totals.campaignCount === 0 ? (
            <div style={styles.muted}>
              No campaigns resolved yet. {status?.canManage ? 'Click “Enrich campaigns” to match ads to GAM orders.' : 'Ask an admin to run campaign enrichment.'}
            </div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.thLeft}>Campaign (GAM Order)</th>
                  <th style={styles.th}>Channels</th>
                  {hasCmsData && <th style={styles.th}>CMS</th>}
                  <th style={styles.th}>Ads</th>
                  <th style={styles.th}>Impressions</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c) => (
                  <tr key={c.gamOrderId} style={styles.tr}>
                    <td style={styles.tdLeft}>
                      {c.campaignName || `Order ${c.gamOrderId}`}
                      {hasCmsData && c.cmsList.filter((x) => x !== 'unknown').length > 1 && (
                        <span style={styles.crossCms}>cross-CMS</span>
                      )}
                    </td>
                    <td style={styles.td}>{fmtInt(c.channelCount)}</td>
                    {hasCmsData && (
                      <td style={styles.td}>{c.cmsList.filter((x) => x !== 'unknown').join(', ') || '—'}</td>
                    )}
                    <td style={styles.td}>{fmtInt(c.recordCount)}</td>
                    <td style={styles.td}>{fmtImpr(c.impressions)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {!loading && !rollup && !error && (
        <div style={styles.muted}>No ad data yet. {status?.canManage ? 'Run a backfill to populate the warehouse.' : ''}</div>
      )}
    </div>
  );
};

const Stat: React.FC<{ label: string; value: string; hint?: string; wide?: boolean }> = ({ label, value, hint, wide }) => (
  <div style={{ ...styles.stat, ...(wide ? { flex: 2 } : {}) }}>
    <div style={styles.statValue}>{value}</div>
    <div style={styles.statLabel}>{label}{hint ? ` · ${hint}` : ''}</div>
  </div>
);

// ---------------------------------------------------------------------------
// Styles (inline, theme tokens with fallbacks — mirrors FinancialsTab)
// ---------------------------------------------------------------------------

const styles: Record<string, React.CSSProperties> = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 12 },
  controls: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
  select: { padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border-1)', background: 'var(--bg-surface-1)', color: 'var(--fg-1)' },
  searchInput: { padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border-1)', minWidth: 240, background: 'var(--bg-surface-1)', color: 'var(--fg-1)' },
  viewToggle: { display: 'flex', border: '1px solid var(--border-1)', borderRadius: 6, overflow: 'hidden' },
  viewBtn: { padding: '6px 12px', border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--fg-2)', fontSize: 13 },
  viewBtnActive: { background: 'var(--accent)', color: '#fff' },
  btn: { padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border-1)', background: 'var(--bg-surface-1)', color: 'var(--fg-1)', cursor: 'pointer' },
  btnPrimary: { padding: '6px 12px', borderRadius: 6, border: 'none', background: 'var(--accent)', color: '#fff', cursor: 'pointer' },
  banner: { padding: '8px 12px', borderRadius: 6, background: 'var(--info-soft)', color: 'var(--info)', fontSize: 13 },
  error: { padding: '8px 12px', borderRadius: 6, background: 'var(--danger-soft)', color: 'var(--danger)', fontSize: 13 },
  stats: { display: 'flex', gap: 10, flexWrap: 'wrap' },
  stat: { flex: 1, minWidth: 120, padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border-1)', background: 'var(--bg-surface-1)' },
  statValue: { fontSize: 18, fontWeight: 700, color: 'var(--fg-1)' },
  statLabel: { fontSize: 11, color: 'var(--fg-2)', textTransform: 'uppercase', letterSpacing: 0.4 },
  tableWrap: { ...tableStyles.wrapScroll, maxHeight: 'calc(100vh - 320px)' },
  table: tableStyles.table,
  th: { ...tableStyles.thSticky, textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  thLeft: tableStyles.thSticky,
  tr: {},
  td: tableStyles.tdNum,
  tdLeft: tableStyles.td,
  tdLeftSmall: { ...tableStyles.td, color: 'var(--fg-2)', fontSize: 12 },
  expandTd: { padding: '0 0 8px 30px', background: 'var(--bg-surface-3)' },
  subTable: { width: '100%', borderCollapse: 'collapse', fontSize: 12.5 },
  subTr: { borderTop: '1px solid var(--border-1)' },
  subTd: { padding: '5px 10px', textAlign: 'right', color: 'var(--fg-1)', whiteSpace: 'nowrap', verticalAlign: 'top' },
  subTdLeft: { padding: '5px 10px', textAlign: 'left', color: 'var(--fg-1)' },
  liTr: { background: 'var(--bg-surface-1)' },
  liName: { fontSize: 12, color: 'var(--fg-2)' },
  campaign: { fontSize: 11.5, color: 'var(--accent)' },
  fmtChip: { display: 'inline-block', padding: '1px 6px', borderRadius: 4, background: 'var(--bg-surface-3)', color: 'var(--fg-2)', fontSize: 11, marginRight: 6 },
  brand: { fontWeight: 600 },
  tag: { marginLeft: 8, padding: '1px 6px', borderRadius: 4, background: 'var(--warning-soft)', color: 'var(--warning)', fontSize: 10 },
  crossCms: { marginLeft: 8, padding: '1px 6px', borderRadius: 4, background: 'var(--accent-soft)', color: 'var(--accent)', fontSize: 10 },
  muted: { padding: 16, color: 'var(--fg-2)', fontSize: 13 },
};

export default DirectSoldAdsTab;
